import {
  Outlet,
  createFileRoute,
  redirect,
  useNavigate,
} from '@tanstack/react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAuth, refreshDepartmentAccess } from '@/stores/auth';
import { isSessionValid } from '@/helpers/auth';
import { ory } from '@/auth';
import { LeftPanel } from '@/components/Menu/LeftPanel';
import { BottomBar } from '@/components/Menu/BottomBar';
import { useTenantID } from '@/hooks/useTenantID';
import { useGetTenant } from '@/api/trafikportalen';
import { useContext, useEffect } from 'react';
import { useConfig } from '@/contexts/ConfigContext';
import {
  INACTIVITY_LOGOUT_KEY,
  useInactivityTimeout,
} from '@/hooks/useInactivityTimeout';
import { checkUserPermissions } from '@/components/Auth/checkUserPermissions';
import { InactivityWarningDialog } from '@/components/InactivityWarningDialog';
import { toast } from '@/hooks/useToast';
import { useListAvailableUserProfiles } from '@/api/auth';
import { ThemeCtx } from '@/contexts/ThemeContext';
import { markProfileAsInvalid } from '@/helpers/invalidProfiles';
import { useFavicon } from '@/hooks/useFavicon';
import { useSearchShortcut } from '@/hooks/useSearchShortcut';

const publicRoutes = ['/$tenantId/login', '/$tenantId/register'];

export const Route = createFileRoute('/$tenantId/_layout')({
  component: Layout,
  beforeLoad: async ({ location, params }) => {
    if (publicRoutes.includes(location.pathname)) {
      return;
    }

    try {
      const session = useAuth.getState().session;
      if (!isSessionValid(session)) {
        // try to get a new session
        const { data: newSession } = await ory.toSession();
        if (!isSessionValid(newSession)) {
          throw Error('session is not valid');
        }
        useAuth.getState().setSession(newSession);
      }
    } catch (e) {
      console.error('not logged in', e);
      throw redirect({
        to: '/$tenantId/login',
        replace: true,
        params: {
          tenantId: params.tenantId,
        },
        search: {
          returnTo: location.href,
        },
      });
    }

    // Enforce inactivity-logout re-authentication. After the inactivity
    // timer fires the Ory session is intentionally kept alive (so the user
    // doesn't have to redo MFA), but the user must still pass through the
    // login page before they can reach any protected route again. The gate
    // is cleared by the login page when the user clicks "Logga in".
    //
    // NOTE: this gate is best-effort only. A user who knows the
    // localStorage key can clear it via DevTools and bypass the gate.
    // Truly tamper-resistant inactivity enforcement is not achievable
    // purely client side while keeping the Ory session alive — it would
    // require either destroying the Ory session on inactivity (which
    // forces full MFA re-entry on every reactivation) or backend support
    // for an inactivity-aware session flag. The real authorization
    // boundary remains the backend, which validates every request against
    // the Ory session cookie.
    if (localStorage.getItem(INACTIVITY_LOGOUT_KEY)) {
      throw redirect({
        to: '/$tenantId/login',
        replace: true,
        params: {
          tenantId: params.tenantId,
        },
        search: {
          returnTo: location.href,
        },
      });
    }

    // Valid session, ensure we have a profile selected
    if (location.pathname !== '/profiles') {
      const { userProfile, setUserProfile, previousUserProfile } =
        useAuth.getState();

      if (!userProfile || params.tenantId !== userProfile.tenant_id) {
        useAuth.getState().logout();
        // no user profile selected, redirect to /profiles. Pass the tenant the
        // user is signing in to so the picker can be skipped when they only
        // have a single profile for it.
        throw redirect({
          to: '/profiles',
          search: {
            tenantId: params.tenantId,
            returnTo: location.href,
          },
        });
      }

      // Check if currently impersonating
      const isImpersonating = !!userProfile?.expires_at;

      if (isImpersonating) {
        const expiresAt = new Date(userProfile.expires_at!);
        const now = new Date();

        if (expiresAt < now) {
          if (previousUserProfile) {
            setUserProfile(previousUserProfile);
            throw redirect({
              to: '/$tenantId',
              replace: true,
              params: {
                tenantId: previousUserProfile?.tenant_id || '',
              },
            });
          } else {
            useAuth.getState().logout();
            throw redirect({
              to: '/$tenantId/login',
              replace: true,
              params: {
                tenantId: params.tenantId,
              },
            });
          }
        }
      }
    }
  },
});

function Layout() {
  const tenantId = useTenantID();
  const { data: tenant, isLoading } = useGetTenant();
  const { departmentAccess, setUserProfile, userProfile, permissions } =
    useAuth();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const config = useConfig();
  const { data: profiles } = useListAvailableUserProfiles({
    application_uri: config.auth.applicationUri,
  });
  const [, setTenantTheme] = useContext(ThemeCtx);
  useFavicon(tenantId);
  useSearchShortcut(tenantId);

  useEffect(() => {
    if (tenantId) {
      setTenantTheme(tenantId);
    }
  }, [tenantId, setTenantTheme]);

  useEffect(() => {
    if (!tenant && !isLoading) {
      navigate({
        to: '/',
        replace: true,
      });
    }
  }, [tenant, isLoading, navigate]);

  useEffect(() => {
    if (isLoading) return;

    if (!checkUserPermissions({ permission: 'tenant:viewer' })) {
      refreshDepartmentAccess();
    }
  }, [isLoading]);

  useEffect(() => {
    if (!profiles || !departmentAccess) return;

    // Only department users depend on department access; org/tenant admins get
    // their access from permissions and legitimately have zero departments.
    // Department users have no permissions by design, so without this guard the
    // check mis-fires when switching to an admin profile (which loads with zero
    // department access) and bounces the user to /profiles.
    const isDepartmentUser = permissions.length === 0;

    if (isDepartmentUser && departmentAccess.accessible_departments === 0) {
      if (userProfile?.id) {
        markProfileAsInvalid(userProfile.id);
      }

      setUserProfile(null);
      if (profiles?.length === 1) {
        navigate({
          to: '/$tenantId',
          replace: true,
          params: { tenantId },
        });
      } else {
        navigate({
          to: '/profiles',
          replace: true,
          search: {
            tenantId,
          },
        });
      }
      toast.warning({
        title: formatMessage(
          {
            defaultMessage:
              'Du har inte längre tillgång till {organizationName}. Vänligen välj en annan profil eller kontakta support om du tror att detta är ett fel.',
          },
          {
            organizationName: userProfile?.organization,
          },
        ),
      });
    }
  }, [
    departmentAccess,
    formatMessage,
    navigate,
    permissions,
    profiles,
    profiles?.length,
    setUserProfile,
    tenantId,
    userProfile?.organization,
    userProfile?.id,
    userProfile?.tenant_id,
  ]);

  const { disabled } = useConfig();

  const { showWarning, remainingSeconds } = useInactivityTimeout();

  if (disabled) {
    return null;
  }

  if (isLoading) {
    return null;
  }

  return (
    <div className="relative flex w-full flex-col-reverse overflow-auto lg:flex-row">
      <a
        href="#main-content"
        className="sr-only absolute left-0 top-0 z-50 rounded bg-primary-600 px-4 py-2 text-primary-50 focus:not-sr-only focus:m-2"
      >
        <FormattedMessage defaultMessage="Hoppa till huvudinnehåll" />
      </a>
      <aside className="fixed inset-y-0 left-0 hidden w-[300px] lg:block">
        <LeftPanel />
      </aside>
      <main
        id="main-content"
        className="mb-16 flex h-screen grow flex-col overflow-hidden lg:mb-0 lg:ml-[300px]"
      >
        <Outlet />
      </main>
      <aside className="fixed inset-x-0 bottom-0 lg:hidden">
        <BottomBar />
      </aside>
      <InactivityWarningDialog
        open={showWarning}
        remainingSeconds={remainingSeconds}
      />
    </div>
  );
}
